import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Button,
  Spinner,
  Alert,
  Badge,
  Row,
  Col,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink
} from "reactstrap"
import {
  getRFQsByDate,
  exportRFQsByDate,
  formatCurrency,
  getStatusColor
} from "../../../../helpers/rfq_dashboard_api"

const TimelineDetailModal = ({ 
  isOpen, 
  toggle, 
  dateData,
  filters = {}
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [exporting, setExporting] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && (dateData?.date || dateData?.month)) {
      setCurrentPage(1) // Reset to first page when modal opens
      loadData(1)
    }
  }, [isOpen, dateData, filters])

  // Keyboard navigation for pagination
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!isOpen || loading) return

      if (event.key === 'ArrowLeft' && pagination?.has_prev) {
        event.preventDefault()
        handlePageChange(pagination.current_page - 1)
      } else if (event.key === 'ArrowRight' && pagination?.has_next) {
        event.preventDefault()
        handlePageChange(pagination.current_page + 1)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress)
      return () => document.removeEventListener('keydown', handleKeyPress)
    }
  }, [isOpen, pagination, loading])

  // Handle page change
  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= (pagination?.total_pages || 1) && !loading) {
      setCurrentPage(page)
      loadData(page)
    }
  }

  const loadData = async (page = currentPage) => {
    try {
      setLoading(true)
      setError(null)

      console.log("Loading RFQs for date/month:", dateData?.date || dateData?.month, "with filters:", filters, "page:", page)

      // Add pagination parameters to filters
      const paginatedFilters = {
        ...filters,
        // Pass month if available, otherwise fall back to date
        ...(dateData?.month ? { month: dateData.month } : { date: dateData?.date }),
        page: page,
        per_page: perPage
      }
      
      const response = await getRFQsByDate(paginatedFilters)
      console.log("Timeline RFQs response:", response)

      if (response && response.status) {
        // Extract the rfqs array and pagination from the nested data structure
        const rfqsData = response.data?.rfqs || []
        const paginationData = response.data?.pagination || null
        
        console.log("Extracted Timeline RFQs data:", rfqsData)
        console.log("Pagination data:", paginationData)
        
        setData(rfqsData)
        setPagination(paginationData)
        console.log("Timeline RFQs data set successfully:", rfqsData)
      } else {
        console.error("API response failed:", response)
        throw new Error(response?.message || "Failed to load RFQ data")
      }
    } catch (err) {
      console.error("Error loading Timeline RFQ data:", err)
      setError(err.message || "Failed to load RFQ data")
      setData([])
      setPagination(null)
    } finally {
      setLoading(false)
    }
  }

  const handleExportCSV = async () => {
    try {
      setExporting(true)

      const exportFilters = {
        ...filters,
        // Pass month if available, otherwise fall back to date
        ...(dateData?.month ? { month: dateData.month } : { date: dateData?.date })
      }

      const response = await exportRFQsByDate(exportFilters)

      if (response && response.status) {
        // Create and trigger download
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `rfqs_${dateData?.date}_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        throw new Error(response?.message || "Failed to export data")
      }
    } catch (err) {
      console.error("Export error:", err)
      setError(err.message || "Failed to export data")
    } finally {
      setExporting(false)
    }
  }

  const getModalTitle = () => {
    if (dateData?.month) {
      return `RFQs Changed in ${dateData.month}`
    }
    return `RFQs Changed on ${dateData?.date || 'Selected Date'}`
  }

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'created':
      case 'rfq created':
        return 'success'
      case 'in progress':
      case 'work in progress':
        return 'warning'
      case 'completed':
      case 'closed':
        return 'info'
      case 'cancelled':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString()
  }

  // Filter data based on search term
  const filteredData = (Array.isArray(data) ? data : []).filter(item => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      item.rfq_number?.toLowerCase().includes(searchLower) ||
      item.title?.toLowerCase().includes(searchLower) ||
      item.project?.name?.toLowerCase().includes(searchLower) ||
      item.vendor?.name?.toLowerCase().includes(searchLower) ||
      item.current_status?.toLowerCase().includes(searchLower) ||
      item.previous_status?.toLowerCase().includes(searchLower)
    )
  })

  // Debug logging
  console.log("TimelineDetailModal render - data:", data)
  console.log("TimelineDetailModal render - filteredData:", filteredData)
  console.log("TimelineDetailModal render - searchTerm:", searchTerm)

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="timeline-detail-modal">
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center">
          <i className="bx bx-line-chart me-2"></i>
          {getModalTitle()}
          {pagination && (
            <Badge color="primary" className="ms-2">
              {pagination.total} Records Found
            </Badge>
          )}
          {!pagination && dateData && (
            <Badge color="secondary" className="ms-2">
              Loading...
            </Badge>
          )}
        </div>
      </ModalHeader>
      <ModalBody>
        {/* Header with Search, Per-Page Selection, and Export */}
        <Row className="mb-3">
          <Col md={6}>
          <div className="search-box">
            <div className="position-relative">
              <Input
                type="text"
                placeholder="Search by RFQ#, title, project, vendor, or status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <i className="bx bx-search-alt search-icon"></i>
            </div>
          </div>
          </Col>
          <Col md={2}></Col>
          <Col md={2}>
            <div className="d-flex align-items-center">
              <label className="form-label mb-0 me-2" style={{fontSize: '0.875rem', whiteSpace: 'nowrap'}}>
                Show:
              </label>
              <Input
                type="select"
                value={perPage}
                onChange={(e) => {
                  const newPerPage = parseInt(e.target.value)
                  setPerPage(newPerPage)
                  setCurrentPage(1) // Reset to first page
                  loadData(1) // Reload with new per page value
                }}
                disabled={loading}
                style={{fontSize: '0.875rem'}}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>
            </div>
          </Col>
          <Col md={2} className="text-end">
            <Button
              color="success"
              onClick={handleExportCSV}
              disabled={exporting || loading}
              block
            >
              {exporting ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Exporting...
                </>
              ) : (
                <>
                  <i className="bx bx-download me-1"></i>
                  Export CSV
                </>
              )}
            </Button>
          </Col>
        </Row>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <div className="mt-2">Loading RFQ data...</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert color="danger">
            <h4 className="alert-heading">Error</h4>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {/* Data Table */}
        {!loading && !error && (
          <>
            {filteredData.length > 0 ? (
              <div className="table-responsive">
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>RFQ#</th>
                      <th>Title</th>
                      <th>Project</th>
                      <th>Vendor</th>
                      <th>Current Status</th>
                      <th>Budget</th>
                      <th>Final Amount</th>
                      <th>Changed At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((rfq, index) => (
                      <tr key={rfq.rfq_id || index}>
                        <td>
                          <strong>{rfq.rfq_number || '-'}</strong>
                        </td>
                        <td>{rfq.title || '-'}</td>
                        <td>{rfq.project?.name || '-'}</td>
                        <td>{rfq.vendor?.name || 'Not Assigned'}</td>
                        <td>
                          <Badge color={getStatusBadgeColor(rfq.current_status)}>
                            {rfq.current_status || '-'}
                          </Badge>
                        </td>
                        <td>{formatCurrency(rfq.budget)}</td>
                        <td>{rfq.final_amount ? formatCurrency(rfq.final_amount) : '--'}</td>
                        <td>{formatDateTime(rfq.status_changed_at || rfq.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-4">
                <i className="ri-time-line display-4 text-muted"></i>
                <h5 className="mt-3">No Status Changes Found</h5>
                <p className="text-muted">
                  {searchTerm ? 'No RFQs match your search criteria.' : 'No status changes found for this date.'}
                </p>
                {!searchTerm && (
                  <small className="text-muted">
                    Data count: {Array.isArray(data) ? data.length : 0} records loaded
                  </small>
                )}
              </div>
            )}
          </>
        )}
        
        {/* Pagination Controls - Always show when there's data */}
        {!loading && !error && data && data.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-3 px-3">
            <div className="text-muted">
              <small>
                {pagination ? (
                  <>
                    Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                    {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                    {pagination.total} entries
                    {pagination.total_pages > 1 && (
                      <span className="ms-2">
                        (Page {pagination.current_page} of {pagination.total_pages})
                      </span>
                    )}
                  </>
                ) : (
                  <>Showing {data.length} entries</>
                )}
              </small>
            </div>
            {pagination && pagination.total_pages > 1 ? (
              <Pagination className="mb-0">
                <PaginationItem disabled={!pagination.has_prev}>
                  <PaginationLink
                    previous
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                  />
                </PaginationItem>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                  let pageNum;
                  if (pagination.total_pages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.current_page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.current_page >= pagination.total_pages - 2) {
                    pageNum = pagination.total_pages - 4 + i;
                  } else {
                    pageNum = pagination.current_page - 2 + i;
                  }

                  return (
                    <PaginationItem key={pageNum} active={pageNum === pagination.current_page}>
                      <PaginationLink onClick={() => handlePageChange(pageNum)}>
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}

                <PaginationItem disabled={!pagination.has_next}>
                  <PaginationLink
                    next
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                  />
                </PaginationItem>
              </Pagination>
            ) : (
              <div className="text-muted">
                <small>Single page - All records displayed</small>
              </div>
            )}
          </div>
        )}


      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default TimelineDetailModal
